import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SFH_RESTRICTION_SYNC } from '../../constants/localStorage';
import { fetchAccountSettings } from '../../store/actions/account/accountActions';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import { retrieveFromLocalStorage } from '../helpers/localStorageHelper';
import { parseSellFromHomeRestrictionSync } from '../helpers/sellFromHomeRestrictionSync';

/**
 * Keeps Sell From Home restriction flags fresh across tabs when another tab
 * broadcasts an SFH lockout (e.g. SSNTooManyAttempts). Also refetches on
 * visibility if a newer sync signal was written while this tab was hidden.
 */
const useSellFromHomeRestrictionSync = () => {
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const lastSyncedAtRef = useRef(0);

  useEffect(() => {
    if (!accountUid) return undefined;

    const refetchIfNewer = (payload) => {
      if (!payload || payload.accountUid !== accountUid) return;

      const syncedAt = Number(payload.at) || 0;
      if (syncedAt <= lastSyncedAtRef.current) return;

      lastSyncedAtRef.current = syncedAt;
      dispatch(fetchAccountSettings({ accountUid }));
    };

    const handleStorage = (event) => {
      if (event.key !== SFH_RESTRICTION_SYNC) return;
      refetchIfNewer(parseSellFromHomeRestrictionSync(event.newValue));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      let stored;
      try {
        stored = retrieveFromLocalStorage(SFH_RESTRICTION_SYNC);
      } catch (error) {
        return;
      }

      refetchIfNewer(stored);
    };

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accountUid, dispatch]);
};

export default useSellFromHomeRestrictionSync;
